import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import query from "devextreme/data/query";
import Form, { Label, SimpleItem, GroupItem, RequiredRule } from '../BootstrapForm';
import DataGrid, { Column, LoadPanel, Scrolling, HeaderFilter, MasterDetail, Toolbar, Item, Paging, Pager } from '../BootstrapDataGrid';
import { DateBox, TextBox, SelectBox, Button } from '../BootstrapControls';
import Axios from "../../api/axios";
import notify from '../../utils/notifyUtil';
import Dict from '../../messages.js';
import * as glb from '../../globals.js';
import * as utl from '../../utils/utils.js';
import { getCached, getCachedOrFetch, setCached, clearCached, carRefCacheKey } from '../../utils/requestCache.js';
import { emitCariSync, subscribeCariSync } from '../../utils/cariSyncBus.js';
import { addMaxLengthToGridColumn,addMaxLengthToEditorOptions } from '../../utils/formValidationHelper.js';
import { nextTempDetayRef, isTempId } from '../../utils/tempIdUtils';
import { normalizeKeysToUpper } from '../../utils/dbKeyNormalize.js';
import { buildPaymentHistoryRows, hasOutstandingBalance } from '../../utils/cariPaymentPresentation.js';
import MasterKayitGerekliUyari, { cariKayitliMi, MSG_ONCE_CARI_KAYDET } from './MasterKayitGerekliUyari.js';
import { QuickDateTimeBox } from '../common/QuickDateBoxes';
import { gridHeightByRowCount } from '../../utils/gridAutoHeight.js';
import { attachFormEnterToNextField } from '../../utils/formEnterFocusNext';
import { aramaArayanRef, canDeleteByOwner, cariDataSahibiRef, FOREIGN_DELETE_MESSAGE, isGeneralYetkili } from '../../utils/ownRecordDelete.js';
import { ensureEntityBundleCached } from '../../services/syncService';
import { asArray } from '../../utils/arrayData.js';
import { getSelectableUsersWithIncluded } from '../../utils/userLookup.js';
import { allowedSatisYapanRefs, canAssignSatisYapan, canEditAramaBySabitYetki, canEditAramaRecord, getScreenPermission } from '../../utils/cariOwnerAccess.js';
import {
    mergeIslemUpdates,
    confirmRandevuOteleme,
    formatRandevuShiftMessage,
    planIslemDateShift,
} from '../../utils/islemRandevuShift';
import IslemGerceklestiCell from './IslemGerceklestiCell';
import {
    enrichMetaFormItems,
    latestMetaFormRef,
    META_FORM_EDITOR_CLASS,
    META_FORM_FIELD_CLASS,
} from '../../utils/metaFormGelis.js';
import './CariAramaPanel.css';

/** Tarih/string farkları ve grid kopyaları yüzünden sonsuz setActiveArama döngüsünü önlemek için */
function aramaRowSyncKey(r) {
    if (!r) return '';
    const ts = (v) => {
        if (v == null || v === '') return '';
        const t = new Date(v).getTime();
        return Number.isFinite(t) ? String(t) : String(v);
    };
    return [
        String(r.CD_REF ?? r.cd_ref ?? ''),
        String(r.CD_ACK ?? '').trim(),
        String(Number(r.CD_VER_FIYAT ?? 0)),
        ts(r.CD_ARA_TAR),
        String(r.CD_DEGERLENDIRME ?? ''),
        String(r.SON_DURUM ?? ''),
    ].join('|');
}

function setGridFocusedRowKeyIfNeeded(gridRef, key) {
    const inst = gridRef?.current?.instance;
    if (!inst) return;
    const cur = inst.option('focusedRowKey');
    if (String(cur ?? '') !== String(key ?? '')) {
        inst.option('focusedRowKey', key);
    }
}

export default function CariAramaPanel({ carRef, carKey, cariAramaList, onListChange, formData, isNewRecord = false, deferHeavyLoads = false }) {
    const [activeArama, setActiveArama] = useState(cariAramaList && cariAramaList.length > 0 ? cariAramaList[0] : null);
    const [heavyReady, setHeavyReady] = useState(!deferHeavyLoads);
    const setAramaDateField = useCallback((field, dateValue) => {
        setActiveArama(prev => ({ ...(prev || {}), [field]: dateValue }));
        setAramaDataChanged(true);
    }, []);
    const [durumOptions, setDurumOptions] = useState(query(glb.default.sabitLkp).filter(x => x.ST_TUR === 15).toArray());
    const [durumManuelSecildi, setDurumManuelSecildi] = useState(false);
    const [aramaReadOnly, setAramaReadOnly] = useState(true);
    const [aramaDataChanged, setAramaDataChanged] = useState(false);
    const [appointmentApprovalOnly, setAppointmentApprovalOnly] = useState(false);
    const [appointmentOriginalDate, setAppointmentOriginalDate] = useState(null);
    const [tahsilatList, setTahsilatList] = useState([]); // satış (CAS_REF) bazlı borç/alacak özeti
    const [selectedTahsilatCasRef, setSelectedTahsilatCasRef] = useState(null);
    const [focusedTahsilatKey, setFocusedTahsilatKey] = useState(null);
    const [islemShowAll, setIslemShowAll] = useState(true);
    const [islemSectionOpen, setIslemSectionOpen] = useState(false);
    const [satisList, setSatisList] = useState([]);
    const satisListRef = useRef(satisList);
    satisListRef.current = satisList;
    const [activeSatis, setActiveSatis] = useState(null);
    const [islemList, setIslemList] = useState([]);
    const [paraRawList, setParaRawList] = useState([]);
    const activeDegerlendirme = useMemo(
        () => (glb.default.sabitLkp || []).find(x => Number(x.ST_REF) === Number(activeArama?.CD_DEG_REF)),
        [activeArama?.CD_DEG_REF]
    );
    const metaFormItems = useMemo(
        () => enrichMetaFormItems(formData?.FORM_GELISLER),
        [formData?.FORM_GELISLER]
    );
    const showMetaFormSelect = metaFormItems.length > 1;
    const kulLookup = useMemo(
        () => query(glb.default.KULLANICILkp || []).toArray(),
        []
    );
    const getKulAdi = useCallback((kulRef) => {
        if (kulRef == null || kulRef === '') return '';
        return kulLookup.find((x) => Number(x.KUL_REF) === Number(kulRef))?.KUL_ADI || '';
    }, [kulLookup]);
    const isAdminOrGeneralAuth = isGeneralYetkili(glb.default.KULLANICI);
    const dynamicUyariYer = Number(activeDegerlendirme?.ST_UYAR_YER);
    // Delphi hata_kont_detay: ST_UYAR_YER 1/3/6 → ileri, 2/3/6 → randevu, 5/6 → satıcı
    const isIleriAramaRequiredByDegerlendirme = dynamicUyariYer === 1 || dynamicUyariYer === 3 || dynamicUyariYer === 6;
    const isRandevuRequiredByDegerlendirme = dynamicUyariYer === 2 || dynamicUyariYer === 3 || dynamicUyariYer === 6;
    const isSaticiRequiredByDegerlendirme = dynamicUyariYer === 5 || dynamicUyariYer === 6;
    const isGeneralYetkiUser = Number(glb.default?.KULLANICI?.KUL_YETKI) === 0;

    // Obje referansına bağlanma: her setActiveArama ile Form/editörler yeniden bağlanıp DX içinde döngü tetiklenebiliyor
    const activeAramaReadonlyFromPara = !!activeArama?._READ_ONLY_FROM_PARA;

    // Helper: field-level access control
    const getFieldReadOnly = useCallback((fieldLabel, fieldName) => {
        if (activeAramaReadonlyFromPara) return true;
        if (aramaReadOnly) return true;
        if (appointmentApprovalOnly && fieldName !== 'CD_RAN_TAR') return true;
        if ((fieldName === 'CD_YETKILI' || fieldName === 'CD_YETK_REF') && Number(activeArama?.CD_REF) > 0) {
            return true;
        }
        return utl.hasFieldRight(`CARIKART Giremez>>ARAMA>>${fieldLabel}>>${fieldName}`);
    }, [aramaReadOnly, appointmentApprovalOnly, activeAramaReadonlyFromPara, activeArama?.CD_REF]);

    // Helper: field-level required control
    const getIsFieldRequired = useCallback((fieldLabel, fieldName) => {
        return utl.hasFieldRight(`CARIKART Zorunlu>>ARAMA>>${fieldLabel}>>${fieldName}`);
    }, []);

    const AramaListGridRef = useRef(null);
    const expandedAramaMasterKeysRef = useRef(new Set());
    const formArama = useRef(null);
    const enterNavCleanupRef = useRef(null);
    const attachEnterNavRoot = useCallback((node) => {
        if (enterNavCleanupRef.current) {
            enterNavCleanupRef.current();
            enterNavCleanupRef.current = null;
        }
        if (node) {
            enterNavCleanupRef.current = attachFormEnterToNextField(node);
        }
    }, []);

    const screen = query(glb.default.EKRANLkp).filter(x => x.EKR_EKRANADI === 'AramaGirPanel').toArray()[0] || { EKLE: true, DUZELT: true, SIL: true };
    useEffect(() => () => {
        if (enterNavCleanupRef.current) {
            enterNavCleanupRef.current();
            enterNavCleanupRef.current = null;
        }
    }, []);
    useEffect(() => {
        expandedAramaMasterKeysRef.current.clear();
        setAramaReadOnly(true);
        setAramaDataChanged(false);
        setDurumManuelSecildi(false);
        const list = Array.isArray(cariAramaList) ? cariAramaList : [];
        setActiveArama(list.length > 0 ? list[0] : null);
        AramaListGridRef.current?.instance?.option('focusedRowIndex', list.length > 0 ? 0 : -1);
        setSelectedTahsilatCasRef(null);
        setFocusedTahsilatKey(null);
        setIslemShowAll(true);
        if (deferHeavyLoads) {
            setHeavyReady(false);
            let idleId = null;
            const t = window.setTimeout(() => {
                if (typeof window.requestIdleCallback === 'function') {
                    idleId = window.requestIdleCallback(() => setHeavyReady(true), { timeout: 400 });
                } else {
                    setHeavyReady(true);
                }
            }, 50);
            return () => {
                window.clearTimeout(t);
                if (idleId != null && typeof window.cancelIdleCallback === 'function') {
                    window.cancelIdleCallback(idleId);
                }
            };
        }
        setHeavyReady(true);
    }, [carRef, deferHeavyLoads]);

    const paraAramaRows = useMemo(
        () => buildPaymentHistoryRows(paraRawList, satisList, glb.default.sabitLkp, glb.default.KULLANICILkp),
        [paraRawList, satisList]
    );

    const applyParaChanges = useCallback((baseList, changes = {}) => {
        const toNum = (v) => Number(v || 0);
        const getRef = (row) => toNum(row?.CP_REF ?? row?.cp_ref ?? row);
        const inserted = Array.isArray(changes.inserted) ? changes.inserted : [];
        const updated = Array.isArray(changes.updated) ? changes.updated : [];
        const deleted = Array.isArray(changes.deleted) ? changes.deleted : [];
        const deletedSet = new Set(deleted.map((x) => getRef(x)));
        let next = (Array.isArray(baseList) ? baseList : []).filter((row) => !deletedSet.has(getRef(row)));
        const upsert = (row) => {
            if (!row || typeof row !== 'object') return;
            const patch = normalizeKeysToUpper(row);
            const id = getRef(patch);
            if (!id) return;
            const idx = next.findIndex((r) => getRef(r) === id);
            if (idx >= 0) next[idx] = { ...next[idx], ...patch };
            else next.unshift(patch);
        };
        inserted.forEach(upsert);
        updated.forEach(upsert);
        return next.sort((a, b) => getRef(b) - getRef(a));
    }, []);
    const decorateIslemRow = useCallback((row, satisRows) => {
        const rs = Array.isArray(satisRows) ? satisRows : [];
        const tipList = query(glb.default.sabitLkp).filter(x => x.ST_TUR === 127).toArray();
        const paketList = query(glb.default.sabitLkp).toArray();
        const sale = rs.find((s) => Number(s?.CAS_REF) === Number(row?.CI_CAS_REF));
        const casTip = Number(row?.CAS_TIP ?? sale?.CAS_TIP ?? 0);
        const stTur = casTip === 1 ? 126 : (casTip === 2 ? 123 : ((casTip === 3 || casTip === 4) ? 124 : 21));
        const tipAd = tipList.find((x) => Number(x.ST_KODINT) === casTip)?.ST_AD || row?.SATIS_TIP_AD || '';
        const paketAd = paketList.find((x) => Number(x.ST_TUR) === stTur && Number(x.ST_KODINT) === Number(row?.CI_TUR))?.ST_AD
            || row?.PAKET_ICERIK_AD || '';
        return {
            ...row,
            CAS_TIP: casTip || row?.CAS_TIP,
            SATIS_TIP_AD: tipAd,
            PAKET_ICERIK_AD: paketAd
        };
    }, []);
    const applyIslemChanges = useCallback((baseList, changes = {}, satisRows = []) => {
        const toNum = (v) => Number(v || 0);
        const inserted = Array.isArray(changes.inserted) ? changes.inserted : [];
        const updated = Array.isArray(changes.updated) ? changes.updated : [];
        const deleted = Array.isArray(changes.deleted) ? changes.deleted : [];
        const deletedSet = new Set(deleted.map((x) => toNum(x?.CI_REF ?? x)));
        let next = (Array.isArray(baseList) ? baseList : []).filter((row) => !deletedSet.has(toNum(row?.CI_REF)));
        const upsert = (row) => {
            const decorated = decorateIslemRow(row, satisRows);
            const id = toNum(decorated?.CI_REF);
            if (!id) return;
            const idx = next.findIndex((r) => toNum(r?.CI_REF) === id);
            if (idx >= 0) next[idx] = { ...next[idx], ...decorated };
            else next.unshift(decorated);
        };
        inserted.forEach(upsert);
        updated.forEach(upsert);
        return next.sort((a, b) => toNum(b?.CI_REF) - toNum(a?.CI_REF));
    }, [decorateIslemRow]);

    const fetchTahsilatList = useCallback(async (forceReload = false) => {
        if (!carRef || carRef <= 0) {
            setTahsilatList([]);
            return;
        }
        const cacheKey = carRefCacheKey('PARA_OZET', carRef);
        if (forceReload) clearCached(cacheKey);
        try {
            const list = await getCachedOrFetch(cacheKey, async () => {
                const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/para-ozet?CAR_REF=${carRef}`);
                return Array.isArray(res?.data) ? res.data : [];
            });
            setTahsilatList(Array.isArray(list) ? list : []);
        } catch (err) {
            console.log('Tahsilat listesi hatası:', err);
            setTahsilatList([]);
        }
    }, [carRef]);

    const fetchParaAramaRows = React.useCallback(async (forceReload = false) => {
        if (!carRef || carRef <= 0) {
            setParaRawList([]);
            return;
        }
        const cacheKey = carRefCacheKey('PARA_LIST_BY_CAR', carRef);
        if (forceReload) clearCached(cacheKey);
        try {
            const list = await getCachedOrFetch(cacheKey, async () => {
                const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/paraList?CAR_REF=${carRef}`);
                return Array.isArray(res?.data) ? res.data : [];
            });
            setParaRawList(Array.isArray(list) ? list : []);
        } catch (err) {
            console.log('Para listesi hatası:', err);
            setParaRawList([]);
        }
    }, [carRef]);

    // Cari satış listesi ve işlem listesi (modül önbelleği – sekme geçişinde tekrar istek yok)
    const fetchSatisVeIslem = React.useCallback(async (forceReload = false) => {
        if (!carRef || carRef <= 0) {
            setSatisList([]);
            setActiveSatis(null);
            setIslemList([]);
            return;
        }
        const cacheKeySatis = carRefCacheKey('SATIS_LST', carRef);
        const cacheKeyIslem = carRefCacheKey('ISLEMLIST_BY_CAR', carRef);
        if (forceReload) {
            clearCached(cacheKeySatis);
            clearCached(cacheKeyIslem);
        }
        try {
            const [list, islemRows] = await Promise.all([
                getCachedOrFetch(cacheKeySatis, async () => {
                    const sl = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/lst?CAR_REF=${carRef}`);
                    return Array.isArray(sl?.data) ? sl.data : [];
                }),
                getCachedOrFetch(cacheKeyIslem, async () => {
                    const il = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/islemlist_by_car?CAR_REF=${carRef}`);
                    return Array.isArray(il?.data) ? il.data : [];
                })
            ]);
            const satisRows = Array.isArray(list) ? list : [];
            setSatisList(satisRows);
            setActiveSatis(satisRows.length > 0 ? satisRows[0] : null);
            setIslemList(Array.isArray(islemRows) ? islemRows : []);
        } catch (err) {
            console.log('Satış/İşlem listesi hatası:', err);
            setSatisList([]);
            setActiveSatis(null);
            setIslemList([]);
        }
    }, [carRef]);

    const persistIslemUpdates = useCallback(async (sourceRow, updates) => {
        const rows = (updates || []).filter((u) => Number(u?.CI_REF ?? 0) > 0);
        if (!rows.length) return false;
        const casRef = Number(sourceRow?.CI_CAS_REF ?? activeSatis?.CAS_REF ?? 0);

        setIslemList((prev) => {
            const next = mergeIslemUpdates(prev, rows);
            setCached(carRefCacheKey('ISLEMLIST_BY_CAR', carRef), next);
            return next;
        });

        try {
            const res = await Axios.post(
                `${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/islemkaydet?cid=${glb.default.KULLANICI.KUL_DEF_FIR_REF}`,
                {
                    inserted: [],
                    updated: rows,
                    deleted: [],
                    casRef,
                    carRef,
                    kulRef: glb.default.KULLANICI.KUL_REF,
                }
            );
            if (!res.data?.success) {
                notify(Dict.get('Error saving changes'), 'error', 2000);
                fetchSatisVeIslem(true);
                return false;
            }
            const serverUpdated = Array.isArray(res.data?.updated) ? res.data.updated : rows;
            let mergedFullRows = [];
            setIslemList((prev) => {
                let next = prev;
                serverUpdated.forEach((row) => {
                    const decorated = decorateIslemRow(row, satisList);
                    next = applyIslemChanges(next, { inserted: [], updated: [decorated], deleted: [] }, satisList);
                });
                setCached(carRefCacheKey('ISLEMLIST_BY_CAR', carRef), next);
                mergedFullRows = serverUpdated.map((row) => {
                    const base = next.find((r) => Number(r.CI_REF) === Number(row.CI_REF)) || row;
                    return decorateIslemRow({ ...base, ...row }, satisList);
                });
                return next;
            });
            emitCariSync({
                carRef,
                casRef,
                source: 'islem',
                islemChanges: { inserted: [], updated: mergedFullRows, deleted: [] },
            });
            notify(Dict.get('Changes saved successfully'), 'success', 1500);
            return true;
        } catch (err) {
            console.error('İşlem güncelleme hatası:', err);
            notify(Dict.get('serverError') + err, 'error', 2000);
            fetchSatisVeIslem(true);
            return false;
        }
    }, [activeSatis, carRef, satisList, applyIslemChanges, decorateIslemRow, fetchSatisVeIslem]);

    const applyAramaIslemDateChange = useCallback(async (row, newValue, previousValue) => {
        if (!row) return false;
        const plan = planIslemDateShift(islemList, row, 'CI_ISL_TAR', newValue, previousValue, {
            casRef: row?.CI_CAS_REF,
        });
        if (plan.needsConfirm && !confirmRandevuOteleme(plan.deltaDays)) return false;
        const key = Number(row?.CI_REF ?? 0);
        if (!key) return false;
        const arrivalIso = newValue instanceof Date ? newValue.toISOString() : newValue;
        const byRef = new Map();
        (plan.updates || []).forEach((u) => byRef.set(Number(u.CI_REF), { ...byRef.get(Number(u.CI_REF)), ...u }));
        byRef.set(key, { ...byRef.get(key), CI_REF: key, CI_ISL_TAR: arrivalIso ?? null });
        const updates = Array.from(byRef.values()).filter((u) => u.CI_REF > 0);
        if (plan.shiftedCount > 0) {
            notify(formatRandevuShiftMessage(plan.shiftedCount, plan.deltaDays), 'info', 2000);
        }
        return persistIslemUpdates(row, updates);
    }, [islemList, persistIslemUpdates]);

    const saveAramaIslemAck = useCallback(async (row, ackValue) => {
        const key = Number(row?.CI_REF ?? 0);
        if (!key) return false;
        const nextAck = String(ackValue ?? '');
        if (nextAck === String(row?.CI_ACK ?? '')) return true;
        return persistIslemUpdates(row, [{ CI_REF: key, CI_ACK: nextAck }]);
    }, [persistIslemUpdates]);

    const saveAramaIslemUpdKul = useCallback(async (row, kulRef) => {
        const key = Number(row?.CI_REF ?? 0);
        if (!key) return false;
        const nextKul = Number(kulRef ?? 0);
        if (nextKul === Number(row?.CI_UPD_KUL ?? 0)) return true;
        return persistIslemUpdates(row, [{ CI_REF: key, CI_UPD_KUL: nextKul || null }]);
    }, [persistIslemUpdates]);

    React.useEffect(() => {
        if (!heavyReady) return;
        let cancelled = false;
        const loadHeavyPanels = async () => {
            // Önce tek kanonik bundle'ı bekle. Başarılıysa aşağıdaki fonksiyonlar
            // requestCache'den bind olur; bundle başarısızsa endpoint fallback'leri çalışır.
            try {
                await ensureEntityBundleCached('cari', carRef);
            } catch (_) { /* endpoint fallback */ }
            if (cancelled) return;
            await Promise.all([
                fetchParaAramaRows(),
                fetchTahsilatList(false),
                fetchSatisVeIslem(),
            ]);
        };
        void loadHeavyPanels();
        return () => { cancelled = true; };
    }, [carRef, heavyReady, fetchParaAramaRows, fetchSatisVeIslem, fetchTahsilatList]);

    React.useEffect(() => {
        const unsub = subscribeCariSync((evt) => {
            if (!carRef || carRef <= 0) return;
            if (Number(evt?.carRef) !== Number(carRef)) return;
            if (evt?.source === 'para') {
                const hasParaPayload = evt?.paraChanges && (Array.isArray(evt?.paraChanges?.inserted) || Array.isArray(evt?.paraChanges?.updated) || Array.isArray(evt?.paraChanges?.deleted));
                if (hasParaPayload) {
                    setParaRawList((prev) => {
                        const next = applyParaChanges(prev, evt.paraChanges);
                        setCached(carRefCacheKey('PARA_LIST_BY_CAR', carRef), next);
                        return next;
                    });
                    if (evt?.paraSummary && evt?.casRef) {
                        setSatisList((prev) => {
                            const rows = Array.isArray(prev) ? [...prev] : [];
                            const idx = rows.findIndex((r) => Number(r?.CAS_REF) === Number(evt.casRef));
                            if (idx >= 0) {
                                rows[idx] = {
                                    ...rows[idx],
                                    ALACAK: Number(evt?.paraSummary?.ALACAK || 0),
                                    TOPLAM_ALINAN: Number(evt?.paraSummary?.TOPLAM_ALINAN || 0),
                                    KALAN: Number(evt?.paraSummary?.KALAN || 0)
                                };
                                setCached(carRefCacheKey('SATIS_LST', carRef), rows);
                            }
                            return rows;
                        });
                    }
                    fetchTahsilatList(true);
                    return;
                }
                // Payload yoksa Arama satırlarını GET'siz, cache'den güncel tut
                const cachedParaByCar = getCached(carRefCacheKey('PARA_LIST_BY_CAR', carRef));
                if (Array.isArray(cachedParaByCar)) {
                    setParaRawList(cachedParaByCar);
                }
                fetchTahsilatList(true);
                return;
            }
            if (evt?.source === 'islem') {
                const hasIslemPayload = evt?.islemChanges && (Array.isArray(evt?.islemChanges?.inserted) || Array.isArray(evt?.islemChanges?.updated) || Array.isArray(evt?.islemChanges?.deleted));
                if (hasIslemPayload) {
                    setIslemList((prev) => {
                        const next = applyIslemChanges(prev, evt.islemChanges, satisListRef.current);
                        setCached(carRefCacheKey('ISLEMLIST_BY_CAR', carRef), next);
                        return next;
                    });
                    return;
                }
                clearCached(carRefCacheKey('ISLEMLIST_BY_CAR', carRef));
                fetchSatisVeIslem();
                return;
            }
            if (evt?.source === 'satis') {
                let satisHandledLocally = false;
                if (Array.isArray(evt?.satisList)) {
                    const satisRows = evt.satisList;
                    setSatisList(satisRows);
                    setActiveSatis(satisRows.length > 0 ? satisRows[0] : null);
                    setCached(carRefCacheKey('SATIS_LST', carRef), satisRows);
                    satisHandledLocally = true;
                } else if (evt?.deletedCasRef) {
                    setSatisList((prev) => {
                        const next = (Array.isArray(prev) ? prev : []).filter((x) => Number(x?.CAS_REF) !== Number(evt.deletedCasRef));
                        setCached(carRefCacheKey('SATIS_LST', carRef), next);
                        return next;
                    });
                    setActiveSatis((prev) => (Number(prev?.CAS_REF) === Number(evt.deletedCasRef) ? null : prev));
                    satisHandledLocally = true;
                } else {
                    const cachedSatis = getCached(carRefCacheKey('SATIS_LST', carRef));
                    if (Array.isArray(cachedSatis)) {
                        setSatisList(cachedSatis);
                        setActiveSatis(cachedSatis.length > 0 ? cachedSatis[0] : null);
                        satisHandledLocally = true;
                    }
                }
                const agr = evt?.autoAgreement;
                if (agr?.row) {
                    setParaRawList((prev) => {
                        const next = applyParaChanges(prev, { inserted: [agr.row], updated: [agr.row], deleted: [] });
                        setCached(carRefCacheKey('PARA_LIST_BY_CAR', carRef), next);
                        return next;
                    });
                } else if (agr && (agr.autoInserted || agr.autoUpdated)) {
                    clearCached(carRefCacheKey('PARA_LIST_BY_CAR', carRef));
                    fetchParaAramaRows(true);
                }
                if (evt?.deletedCasRef) {
                    setParaRawList((prev) => {
                        const next = (Array.isArray(prev) ? prev : []).filter((x) => Number(x?.CP_CAS_REF) !== Number(evt.deletedCasRef));
                        setCached(carRefCacheKey('PARA_LIST_BY_CAR', carRef), next);
                        return next;
                    });
                    setIslemList((prev) => {
                        const next = (Array.isArray(prev) ? prev : []).filter((x) => Number(x?.CI_CAS_REF) !== Number(evt.deletedCasRef));
                        setCached(carRefCacheKey('ISLEMLIST_BY_CAR', carRef), next);
                        return next;
                    });
                }
                fetchTahsilatList(true);
                if (!satisHandledLocally) {
                    clearCached(carRefCacheKey('SATIS_LST', carRef));
                }
            }
        });
        return () => unsub();
    }, [carRef, fetchSatisVeIslem, fetchParaAramaRows, applyParaChanges, applyIslemChanges, fetchTahsilatList]);

    const mergedAramaList = useMemo(() => {
        const base = Array.isArray(cariAramaList) ? cariAramaList : [];
        const paraRows = Array.isArray(paraAramaRows) ? paraAramaRows : [];
        const merged = [...paraRows, ...base];
        return merged.sort((a, b) => {
            const ta = a?.CD_ARA_TAR ? new Date(a.CD_ARA_TAR).getTime() : 0;
            const tb = b?.CD_ARA_TAR ? new Date(b.CD_ARA_TAR).getTime() : 0;
            return tb - ta;
        });
    }, [cariAramaList, paraAramaRows]);

    const isAramaDraftRow = useCallback((row) => {
        if (!row || row._READ_ONLY_FROM_PARA) return false;
        const ref = row.CD_REF ?? row.cd_ref;
        return Number(ref) === -1 || isTempId(ref);
    }, []);

    const activeAramaRef = useRef(activeArama);
    activeAramaRef.current = activeArama;

    // activeArama bağımlılığı YOK: her activeArama güncellemesinde tekrar çalışıp grid↔state döngüsü oluşturuyordu
    React.useEffect(() => {
        const cur = activeAramaRef.current;
        if (isAramaDraftRow(cur)) return;
        if (!aramaReadOnly && !cur?._READ_ONLY_FROM_PARA) return;
        const rows = Array.isArray(mergedAramaList) ? mergedAramaList : [];
        const getRef = (r) => String(r?.CD_REF ?? r?.cd_ref ?? '');
        if (rows.length === 0) {
            if (cur) setActiveArama(null);
            const grid = AramaListGridRef.current?.instance;
            if (grid && grid.option('focusedRowIndex') !== -1) {
                grid.option('focusedRowIndex', -1);
            }
            return;
        }
        const curRef = getRef(cur);
        if (!curRef) {
            const first = rows[0];
            if (aramaRowSyncKey(first) !== aramaRowSyncKey(cur)) {
                setActiveArama(first);
            }
            setGridFocusedRowKeyIfNeeded(AramaListGridRef, first?.CD_REF ?? first?.cd_ref);
            return;
        }
        const matched = rows.find((r) => getRef(r) === curRef);
        if (!matched) {
            const first = rows[0];
            if (aramaRowSyncKey(first) !== aramaRowSyncKey(cur)) {
                setActiveArama(first);
            }
            setGridFocusedRowKeyIfNeeded(AramaListGridRef, first?.CD_REF ?? first?.cd_ref);
            return;
        }
        if (matched !== cur && aramaRowSyncKey(matched) !== aramaRowSyncKey(cur)) {
            setActiveArama(matched);
        }
    }, [mergedAramaList, aramaReadOnly, isAramaDraftRow]);

    const displayedIslemList = useMemo(() => {
        const base = Array.isArray(islemList) ? [...islemList] : [];
        const byRandevu = (a, b) => {
            const at = a?.CI_RAN_TAR ? new Date(a.CI_RAN_TAR).getTime() : Number.POSITIVE_INFINITY;
            const bt = b?.CI_RAN_TAR ? new Date(b.CI_RAN_TAR).getTime() : Number.POSITIVE_INFINITY;
            return at - bt || Number(a?.CI_REF || 0) - Number(b?.CI_REF || 0);
        };
        if (islemShowAll || !selectedTahsilatCasRef) return base.sort(byRandevu);
        const casRef = Number(selectedTahsilatCasRef);
        return base.filter((r) => Number(r?.CI_CAS_REF ?? 0) === casRef).sort(byRandevu);
    }, [islemList, islemShowAll, selectedTahsilatCasRef]);

    const tahsilatGridHeight = useMemo(
        () => gridHeightByRowCount((tahsilatList || []).length, 110),
        [tahsilatList]
    );

    const islemGridHeight = useMemo(
        () => gridHeightByRowCount((displayedIslemList || []).length, 320),
        [displayedIslemList]
    );

    const aramaGridHeight = useMemo(
        () => {
            const rowCount = (mergedAramaList || []).length;
            if (rowCount === 0) return gridHeightByRowCount(0, 600);
            return Math.max(600, gridHeightByRowCount(rowCount, 900));
        },
        [mergedAramaList]
    );

    const handleTahsilatRowFocus = useCallback((row) => {
        if (!row) return;
        const casRef = Number(row.CAS_REF ?? 0);
        if (!casRef) return;
        setSelectedTahsilatCasRef(casRef);
        setFocusedTahsilatKey(casRef);
        setIslemShowAll(false);
        const sale = (satisList || []).find((s) => Number(s?.CAS_REF) === casRef);
        if (sale) setActiveSatis(sale);
    }, [satisList]);

    const formatSignedMoney = React.useCallback((v) => {
        const n = Number(v) || 0;
        const sign = n > 0 ? '+' : '';
        return `${sign}${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, []);

    const aramaValidate = () => {
        if (!formArama.current) return false;
        const res = formArama.current.instance.validate();
        return res.brokenRules.length > 0;
    };

    const aramaFieldDataChanged = () => {
        setAramaDataChanged(true);
    };

    const collectDelphiAramaMissing = useCallback((row) => {
        const missing = [];
        if (!Number(row?.CD_YETK_REF)) missing.push('Yetkili');
        if (!Number(row?.CD_DEG_REF)) missing.push('Değerlendirme');

        if (dynamicUyariYer === 6) {
            if (!row?.CD_ILERI_ARA || !row?.CD_RAN_TAR || !Number(row?.CD_SAT_REF)) {
                missing.push('Bu Değerlendirmede, İleri Tarih, Randevu Tarihi, Satıcıyı Girmelisiniz');
            }
        } else {
            if (isSaticiRequiredByDegerlendirme && !Number(row?.CD_SAT_REF)) {
                missing.push('Bu Değerlendirmede, Satıcıyı girmelisiniz');
            }
            if (isIleriAramaRequiredByDegerlendirme && !row?.CD_ILERI_ARA) {
                missing.push('Bu Değerlendirmede, İleri Tarih Arama Girmelisiniz');
            }
            if (isRandevuRequiredByDegerlendirme && !row?.CD_RAN_TAR) {
                missing.push('Bu Değerlendirmede, Randevu Tarihi Girmelisiniz');
            }
        }

        if (row?.CD_RAN_TAR) {
            const raw = String(row.CD_RAN_TAR);
            const m = raw.match(/(?:T|\s)(\d{1,2}):(\d{2})(?::(\d{2}))?/);
            const isMidnight = m
                ? (Number(m[1]) === 0 && Number(m[2]) === 0)
                : (() => {
                    const d = new Date(row.CD_RAN_TAR);
                    return Number.isFinite(d.getTime())
                        && d.getHours() === 0 && d.getMinutes() === 0;
                })();
            if (isMidnight) missing.push('Randevu Saati Boş Geçilemez');
        }
        if (getIsFieldRequired('Arama Tarihi', 'CD_ARA_TAR') && !row?.CD_ARA_TAR) {
            missing.push(Dict.get('Arama Tarihi'));
        }
        if (getIsFieldRequired('Açıklama', 'CD_ACK') && !String(row?.CD_ACK || '').trim()) {
            missing.push(Dict.get('Açıklama'));
        }
        return missing;
    }, [
        isSaticiRequiredByDegerlendirme,
        isIleriAramaRequiredByDegerlendirme,
        isRandevuRequiredByDegerlendirme,
        dynamicUyariYer,
        getIsFieldRequired,
    ]);

    const handleSave = async () => {
        if (activeArama?._READ_ONLY_FROM_PARA) {
            notify('Para ekranından gelen kayıtlar Arama ekranında değiştirilemez.', 'warning', 2000);
            return;
        }
        if (!cariKayitliMi(carRef)) {
            notify(MSG_ONCE_CARI_KAYDET, 'warning', 2000);
            return;
        }
        if (!aramaDataChanged) {
            notify(Dict.get('Değişiklik Bulunamadı'), 'error', 2000);
            return;
        }

        if (appointmentApprovalOnly) {
            if (!activeArama?.CD_RAN_TAR) {
                notify('Randevu tarihi boş geçilemez.', 'error', 2500);
                return;
            }
            try {
                const response = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/onay/randevu_talep`, {
                    detailRef: Number(activeArama.CD_REF),
                    requestedDate: activeArama.CD_RAN_TAR,
                });
                notify(response?.data?.created === false
                    ? 'Bekleyen randevu tarihi talebiniz yeni tarihle güncellendi.'
                    : 'Randevu tarihi değişikliği genel yetkili onayına gönderildi.', 'success', 3500);
                setActiveArama((prev) => ({ ...(prev || {}), CD_RAN_TAR: appointmentOriginalDate }));
                setAppointmentApprovalOnly(false);
                setAppointmentOriginalDate(null);
                setAramaDataChanged(false);
                setAramaReadOnly(true);
                window.dispatchEvent(new Event('approval-notifications-refresh'));
            } catch (err) {
                notify(err?.response?.data?.message || 'Randevu tarihi onaya gönderilemedi.', 'error', 4000);
            }
            return;
        }

        // Değerlendirme ileri arama tarihini zorunlu kılıyorsa yetkili onayıyla da atlanamaz.
        if (isIleriAramaRequiredByDegerlendirme && !activeArama?.CD_ILERI_ARA) {
            notify('Bu Değerlendirmede, İleri Tarih Arama Girmelisiniz — Boş Geçemezsiniz', 'error', 3500);
            return;
        }
        const saveUser = Number(glb.default?.KULLANICI?.KUL_REF || 0);
        const saveOwner = cariDataSahibiRef(formData);
        const saveGeneral = isGeneralYetkili(glb.default?.KULLANICI);
        const previousSatici = (cariAramaList || []).find((row) => Number(row?.CD_REF) === Number(activeArama?.CD_REF))?.CD_SAT_REF;
        if (Number(activeArama?.CD_SAT_REF) > 0 && !canAssignSatisYapan({
            currentUser: saveUser,
            dataOwner: saveOwner,
            isGeneralAuth: saveGeneral,
        }, activeArama.CD_SAT_REF, previousSatici)) {
            notify('Satıcı yalnız siz veya data sahibi olabilir.', 'warning', 3000);
            return;
        }

        const missingFields = collectDelphiAramaMissing(activeArama);
        if (missingFields.length > 0) {
            // Delphi: genel yetkili (KUL_YETKI=0) onaylarsa yine de kaydedebilir
            if (isGeneralYetkiUser) {
                const ok = window.confirm(
                    `${missingFields.join('\n')}\nBoş Alanları Doldurmadınız\nYinede kayıt Yapılsın mı?`
                );
                if (!ok) return;
            } else {
                notify(`${missingFields.join(', ')} — Boş Geçemezsiniz`, 'error', 3500);
                return;
            }
        } else if (aramaValidate()) {
            notify(Dict.get('Eksik alanları doldurunuz'), 'error', 2000);
            return;
        }

        try {
            const payload = { ...(activeArama || {}) };
            // Legacy *_SAAT alanları karışıklık yaratabiliyor; tek kaynak datetime alanlarını gönder.
            delete payload.CD_ARA_SAAT;
            delete payload.CD_ILERI_ARA_SAAT;
            delete payload.CD_RAN_SAAT;
            const res = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_KART/arakaydet?cid=${glb.default.KULLANICI.KUL_DEF_FIR_REF}`, payload);

            if (res.data.iserr === true) {
                notify(Dict.get('serverError') + res.data.errmsg, 'error', 2000);
            } else {
                const saved = res.data.saved;
                if (!saved) {
                    notify(Dict.get('serverError') + ' (Kayıt dönmedi)', 'error', 2000);
                    return;
                }
                const existingRow = (Array.isArray(cariAramaList) ? cariAramaList : []).find(
                    r => Number(r?.CD_REF ?? r?.cd_ref ?? 0) === Number(activeArama?.CD_REF ?? activeArama?.cd_ref ?? 0)
                ) || {};
                const yetkiliAdi = query(glb.default.KULLANICILkp)
                    .filter(x => Number(x.KUL_REF) === Number(saved?.CD_YETK_REF ?? activeArama?.CD_YETK_REF))
                    .toArray()[0]?.KUL_ADI || '';
                const degerlendirmeRow = query(glb.default.sabitLkp)
                    .filter(x => x.ST_TUR === 5 && Number(x.ST_REF) === Number(saved?.CD_DEG_REF ?? activeArama?.CD_DEG_REF))
                    .toArray()[0] || null;
                const durumRow = query(glb.default.sabitLkp)
                    .filter(x => x.ST_TUR === 15 && Number(x.ST_REF) === Number(saved?.CD_DURUM_REF ?? activeArama?.CD_DURUM_REF))
                    .toArray()[0] || null;
                const savedDecorated = {
                    ...saved,
                    CD_YETKILI: saved?.CD_YETKILI ?? existingRow?.CD_YETKILI ?? yetkiliAdi,
                    CD_DEGERLENDIRME: saved?.CD_DEGERLENDIRME ?? existingRow?.CD_DEGERLENDIRME ?? degerlendirmeRow?.ST_AD ?? '',
                    SON_DURUM: saved?.SON_DURUM ?? existingRow?.SON_DURUM ?? durumRow?.ST_AD ?? '',
                    ST_UYAR_YER: saved?.ST_UYAR_YER ?? existingRow?.ST_UYAR_YER ?? degerlendirmeRow?.ST_UYAR_YER ?? null,
                    ST_YETKI: saved?.ST_YETKI ?? existingRow?.ST_YETKI ?? degerlendirmeRow?.ST_YETKI ?? null
                };
                const savedRef = Number(saved.CD_REF ?? saved.cd_ref ?? 0);
                const oldRef = Number(activeArama?.CD_REF ?? activeArama?.cd_ref ?? 0);
                // Yeni arama kaydı kalp rozetini düşürür; header/list anında tazelensin
                if (!(oldRef > 0) && savedRef > 0) {
                    try {
                        window.dispatchEvent(new CustomEvent('cari-atama-bildirim-refresh'));
                    } catch (_) { /* ignore */ }
                }
                const currentList = Array.isArray(cariAramaList) ? [...cariAramaList] : [];

                // Backend diğer tarihleri temizler; aynı sonucu ekranda yeni GET beklemeden göster.
                const savedIleriTs = savedDecorated?.CD_ILERI_ARA
                    ? new Date(savedDecorated.CD_ILERI_ARA).getTime()
                    : 0;
                if (Number.isFinite(savedIleriTs) && savedIleriTs > Date.now()) {
                    currentList.forEach((row) => {
                        const rowRef = Number(row?.CD_REF ?? row?.cd_ref ?? 0);
                        if (rowRef !== savedRef && row?.CD_ILERI_ARA) row.CD_ILERI_ARA = null;
                    });
                }

                const idxBySavedRef = currentList.findIndex(r => Number(r?.CD_REF ?? r?.cd_ref ?? 0) === savedRef);
                const idxByOldRef = currentList.findIndex(r => Number(r?.CD_REF ?? r?.cd_ref ?? 0) === oldRef);
                if (idxBySavedRef >= 0) {
                    currentList[idxBySavedRef] = { ...currentList[idxBySavedRef], ...savedDecorated };
                } else if (idxByOldRef >= 0) {
                    currentList[idxByOldRef] = { ...currentList[idxByOldRef], ...savedDecorated };
                } else {
                    currentList.push(savedDecorated);
                }

                const toTs = (v) => {
                    const t = v ? new Date(v).getTime() : 0;
                    return Number.isFinite(t) ? t : 0;
                };
                currentList.sort((a, b) => toTs(b?.CD_ARA_TAR) - toTs(a?.CD_ARA_TAR));

                onListChange(currentList);
                setAramaReadOnly(true);
                setAramaDataChanged(false);
                setActiveArama(savedDecorated);
                // mergedAramaList içinde para satırları da olabileceği için focusedRowKey ile odakla
                AramaListGridRef.current?.instance?.option('focusedRowKey', savedRef || (saved.CD_REF ?? saved.cd_ref));
                AramaListGridRef.current?.instance?.refresh();
                // Arama kaydında global sync yayınlamıyoruz;
                // aksi halde diğer sekmeler gereksiz GET zinciri başlatıyor.
            }
        } catch (err) {
            notify(Dict.get('serverError') + err, 'error', 2000);
        }
    };

    const handleYeni = () => {
        if (!cariKayitliMi(carRef)) {
            notify(MSG_ONCE_CARI_KAYDET, 'warning', 2000);
            return;
        }
        AramaListGridRef.current?.instance?.option('focusedRowIndex', -1);
        setAramaReadOnly(false);
        const gelisList = Array.isArray(formData?.FORM_GELISLER) ? formData.FORM_GELISLER : [];
        setActiveArama({
            CD_REF: carKey != null ? nextTempDetayRef() : -1,
            CD_CARI_REF: carRef,
            CD_PER_KUL: glb.default.KULLANICI.KUL_REF,
            CD_PER_SIL: 0,
            CD_UYAR: 0,
            CD_ILERI_ARA_OK: 0,
            CD_YETK_REF: glb.default.KULLANICI.KUL_REF,
            CD_ARA_TAR: new Date(),
            CD_DEG_REF: null,
            CD_ILERI_ARA: null,
            CD_VER_FIYAT: null,
            CD_RAN_TAR: null,
            CD_GER_DON_REF: null,
            CD_ACK: ' ',
            CD_DURUM_REF: null,
            CD_DIGER_REF: null,
            CD_UYAR_REF: null,
            CD_SMS: null,
            CD_RAN_REF: null,
            CD_SAT_REF: null,
            CD_ODEME_TUR: null,
            CD_CFG_REF: latestMetaFormRef(gelisList),
            CD_FIR_REF: glb.default.KULLANICI.KUL_DEF_FIR_REF,
            ST_UYAR_YER: -99,
            ST_YETKI: -99
        });
    };

    const handleDuzenle = () => {
        if (activeArama?._READ_ONLY_FROM_PARA) {
            notify('Para ekranından gelen kayıtlar Arama ekranında değiştirilemez.', 'warning', 2000);
            return;
        }
        const stYetki = Number(activeArama?.ST_YETKI ?? activeArama?.YETKI ?? 0);
        const isGeneralAuthUser = isGeneralYetkili(glb.default?.KULLANICI);
        const currentUser = Number(glb.default?.KULLANICI?.KUL_REF || 0);
        const aramaUser = Number(activeArama?.CD_YETK_REF ?? activeArama?.cd_yetk_ref ?? 0);

        if (!canEditAramaRecord({ currentUser, aramaUser, isGeneralAuth: isGeneralAuthUser, stYetki })) {
            notify(
                currentUser > 0 && currentUser === aramaUser
                    ? 'Bu değerlendirme sabit tanımında Yetki Değiştirebilir olmadığı için değiştirilemez.'
                    : 'Bu aramayı yalnız arayan kişi düzenleyebilir.',
                'warning',
                2500,
            );
            return;
        }

        setAramaReadOnly(false);
    };

    const handleVazgec = async () => {
        setAppointmentApprovalOnly(false);
        setAppointmentOriginalDate(null);
        if (activeArama && isAramaDraftRow(activeArama)) {
            if (cariAramaList && cariAramaList.length > 0) {
                AramaListGridRef.current?.instance?.option('focusedRowIndex', 0);
                setActiveArama(cariAramaList[0]);
            } else {
                setActiveArama(null);
            }
            setAramaDataChanged(false);
            setAramaReadOnly(true);
        } else {
            try {
                const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_KART/ararec?id=${activeArama.CD_REF}&cid=${glb.default.KULLANICI.KUL_DEF_FIR_REF}`);

                if (res.data.iserr === true) {
                    notify(Dict.get('serverError') + res.data.errmsg, 'error', 2000);
                } else {
                    const idx = AramaListGridRef.current?.instance?.option('focusedRowIndex');
                    if (typeof idx === 'number' && idx >= 0) {
                        const newList = [...cariAramaList];
                        newList[idx] = res.data;
                        onListChange(newList);
                    }
                    setActiveArama(res.data);
                    setAramaDataChanged(false);
                    setAramaReadOnly(true);
                    AramaListGridRef.current?.instance?.refresh();
                }
            } catch (err) {
                notify(Dict.get('serverError') + err, 'error', 2000);
            }
        }
    };

    const handleAramaYenileGet = async () => {
        if (!cariKayitliMi(carRef)) {
            notify(MSG_ONCE_CARI_KAYDET, 'warning', 2000);
            return;
        }
        try {
            const cid = glb.default.KULLANICI.KUL_DEF_FIR_REF;
            const [aramaRes] = await Promise.all([
                Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_KART/araList?CAR_REF=${carRef}&cid=${cid}`),
                fetchParaAramaRows(true),
                fetchSatisVeIslem(true),
                fetchTahsilatList(true)
            ]);

            const refreshedAramaList = Array.isArray(aramaRes?.data) ? aramaRes.data : [];
            onListChange(refreshedAramaList);
            setAramaReadOnly(true);
            setAramaDataChanged(false);

            if (refreshedAramaList.length === 0) {
                setActiveArama(null);
                AramaListGridRef.current?.instance?.option('focusedRowIndex', -1);
            } else {
                setActiveArama(refreshedAramaList[0]);
                AramaListGridRef.current?.instance?.option('focusedRowKey', refreshedAramaList[0]?.CD_REF ?? refreshedAramaList[0]?.cd_ref);
            }
            AramaListGridRef.current?.instance?.refresh();
        } catch (err) {
            notify(Dict.get('serverError') + err, 'error', 2000);
        }
    };

    const screenCanEdit = getScreenPermission(screen, 'DUZELT', true);

    // Yeni arama, roldeki EKLE kutusuna bağlı değil. Cari kayıtlıysa ve form okuma
    // modundaysa Yeni Arama görünür. Düzenleme ayrıca kayıt sahibi kuralına bağlıdır.
    const canCreate = cariKayitliMi(carRef) && aramaReadOnly;
    const currentUser = Number(glb.default?.KULLANICI?.KUL_REF || 0);
    const customerOwner = cariDataSahibiRef(formData);
    const aramaUser = Number(activeArama?.CD_YETK_REF ?? activeArama?.cd_yetk_ref ?? 0);
    const activeStYetki = Number(activeArama?.ST_YETKI ?? activeArama?.YETKI ?? 0);
    const isOwnerMatchAllowed = canEditAramaRecord({
        currentUser,
        aramaUser,
        isGeneralAuth: isAdminOrGeneralAuth,
        stYetki: activeStYetki,
    });
    const isSabitEditAllowed = canEditAramaBySabitYetki({
        stYetki: activeStYetki,
        isGeneralAuth: isAdminOrGeneralAuth,
    });
    const canEdit = cariKayitliMi(carRef) && screenCanEdit && mergedAramaList && mergedAramaList.length > 0 && aramaReadOnly && activeArama && !activeArama?._READ_ONLY_FROM_PARA && isOwnerMatchAllowed && isSabitEditAllowed;
    const saticiRefs = allowedSatisYapanRefs({
        currentUser,
        dataOwner: customerOwner,
        isGeneralAuth: isAdminOrGeneralAuth,
    });
    const saticiKaynak = saticiRefs == null
        ? glb.default.KULLANICILkp
        : asArray(glb.default.KULLANICILkp).filter((user) => saticiRefs.includes(Number(user?.KUL_REF)));
    const saticiKullaniciItems = getSelectableUsersWithIncluded(saticiKaynak, [activeArama?.CD_SAT_REF]);

    // Yeni kayıt kontrolü - cari DB'de yoksa arama eklenemez.
    // Ancak ekran yetki verisi üretilemezse, güvenli varsayım olarak erişime izin verilir.
    if (!cariKayitliMi(carRef) || isNewRecord) {
        return <MasterKayitGerekliUyari carRef={carRef} sekmeAdi="Arama İşlemleri" icon="📞" baslik={Dict.get('Arama Kaydı Eklenemez')} />;
    }

    return (
        <div ref={attachEnterNavRoot} className="cari-arama-panel">
            <div className="cari-arama-panel__header">
                <div className="cari-arama-panel__title">
                    <span>📞</span>
                    <span>Arama İşlemleri</span>
                </div>
                <div className="cari-arama-panel__actions">
                    {cariKayitliMi(carRef) && screenCanEdit && !aramaReadOnly && activeArama && (
                        <Button text={appointmentApprovalOnly ? 'Onaya Gönder' : Dict.get('Arama Kaydet')} type="success" onClick={handleSave} />
                    )}
                    {canCreate && (
                        <Button text={Dict.get('Yeni Arama')} type="default" onClick={handleYeni} />
                    )}
                    {canEdit && (
                        <Button text={Dict.get('Arama Düzenle')} type="success" onClick={handleDuzenle} />
                    )}
                    {cariKayitliMi(carRef) && screenCanEdit && !aramaReadOnly && activeArama && (
                        <Button text={Dict.get('Arama Vazgeç')} type="warning" onClick={handleVazgec} />
                    )}
                    {cariKayitliMi(carRef) && activeArama && Number(activeArama.CD_REF) > 0
                        && canDeleteByOwner(glb.default?.KULLANICI, aramaArayanRef(activeArama)) && (
                        <Button text="Arama Sil" type="danger" onClick={async () => {
                            if (!window.confirm('Bu arama kaydı silinsin mi?')) return;
                            try {
                                await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_KART/arasil`, {
                                    CD_REF: activeArama.CD_REF,
                                    kulid: glb.default.KULLANICI.KUL_REF,
                                });
                                notify('Arama silindi.', 'success', 2000);
                                handleAramaYenileGet();
                            } catch (err) {
                                notify(err?.response?.data?.errmsg || FOREIGN_DELETE_MESSAGE, 'error', 3000);
                            }
                        }} />
                    )}
                    {cariKayitliMi(carRef) && (
                        <Button text={Dict.get('Yenile')} type="default" onClick={handleAramaYenileGet} />
                    )}
                </div>
            </div>

            <Form
                ref={formArama}
                formData={activeArama || {}}
                showValidationSummary={true}
                labelLocation='left'
                colCount={1}
                scrollingEnabled={false}
                showColonAfterLabel={true}
                readOnly={aramaReadOnly || !!activeArama?._READ_ONLY_FROM_PARA}
                hoverStateEnabled={false}
                onFieldDataChanged={aramaFieldDataChanged}
            >
                <GroupItem>
                    <GroupItem colCount={2}>
                        <SimpleItem cssClass="cari-arama-panel__date">
                            <Label text={Dict.get('Arama Tarihi')} />
                            <QuickDateTimeBox
                                value={activeArama?.CD_ARA_TAR}
                                readOnly={getFieldReadOnly('Arama Tarihi', 'CD_ARA_TAR')}
                                onValueChanged={(v) => setAramaDateField('CD_ARA_TAR', v)}
                            />
                        </SimpleItem>
                        <SimpleItem dataField='CD_YETK_REF' editorType='dxSelectBox' editorOptions={{
                            readOnly: getFieldReadOnly('Arayan Kişi', 'CD_YETKILI'),
                            items: query(glb.default.KULLANICILkp).toArray(),
                            searchEnabled: true, searchMode: 'contains', displayExpr: 'KUL_ADI', searchExpr: 'KUL_ADI', valueExpr: 'KUL_REF'
                        }}>
                            {!isGeneralYetkiUser && <RequiredRule message={Dict.get('Arayan Kişi') + ' ' + Dict.get('isRequired')} />}
                            <Label text={Dict.get('Arayan Kişi')}></Label>
                        </SimpleItem>
                    </GroupItem>
                    <GroupItem colCount={2}>
                        <SimpleItem dataField='CD_DEG_REF' editorType='dxSelectBox' editorOptions={{
                            readOnly: getFieldReadOnly('Değerlendirme', 'CD_DEGERLENDIRME'),
                            items: query(glb.default.sabitLkp).filter(x => x.ST_TUR === 5).toArray(),
                            searchEnabled: true, searchMode: 'contains', displayExpr: 'ST_AD', searchExpr: 'ST_AD', valueExpr: 'ST_REF',
                            onValueChanged: (e) => {
                                const secilenDegerlendirme = glb.default.sabitLkp.find(x => x.ST_REF === e.value);
                                if (secilenDegerlendirme && secilenDegerlendirme.ST_UST_REF) {
                                    const yeniDurumlar = query(glb.default.sabitLkp).filter(x => x.ST_TUR === 15 && x.ST_REF === secilenDegerlendirme.ST_UST_REF).toArray();
                                    setDurumOptions(yeniDurumlar);
                                    setDurumManuelSecildi(false);
                                    // CD_DURUM_REF'i otomatik olarak yeni durumun ST_REF'i ile set et
                                    if (yeniDurumlar.length > 0) {
                                        setActiveArama(prev => ({ ...prev, CD_DURUM_REF: yeniDurumlar[0].ST_REF }));
                                    } else {
                                        setActiveArama(prev => ({ ...prev, CD_DURUM_REF: null }));
                                    }
                                } else {
                                    setDurumOptions(query(glb.default.sabitLkp).filter(x => x.ST_TUR === 15).toArray());
                                    setActiveArama(prev => ({ ...prev, CD_DURUM_REF: null }));
                                }
                            }
                        }}>
                            {!isGeneralYetkiUser && <RequiredRule message={Dict.get('Değerlendirme') + ' ' + Dict.get('isRequired')} />}
                            <Label text={Dict.get('Değerlendirme')}></Label>
                        </SimpleItem>
                        <SimpleItem dataField='CD_DURUM_REF' editorType='dxSelectBox' editorOptions={{
                            readOnly: getFieldReadOnly('Son Durum', 'SON_DURUM'),
                            items: durumOptions,
                            searchEnabled: true, searchMode: 'contains', displayExpr: 'ST_AD', searchExpr: 'ST_AD', valueExpr: 'ST_REF',
                            onValueChanged: (e) => {
                                // Kullanıcı elle seçim yaptıysa flag'i ata
                                setDurumManuelSecildi(true);
                            }
                        }}>
                            {getIsFieldRequired('Son Durum', 'SON_DURUM') && <RequiredRule message={Dict.get('Son Durum') + ' ' + Dict.get('isRequired')} />}
                            <Label text={Dict.get('Durum')}></Label>
                        </SimpleItem>
                    </GroupItem>
                    <GroupItem colCount={2}>
                        <SimpleItem cssClass="cari-arama-panel__date">
                            <Label text={Dict.get('İleri Tar.Arama')}></Label>
                            <QuickDateTimeBox
                                value={activeArama?.CD_ILERI_ARA}
                                readOnly={getFieldReadOnly('İleri Tar.Arama', 'CD_ILERI_ARA')}
                                onValueChanged={(v) => setAramaDateField('CD_ILERI_ARA', v)}
                            />
                            {(getIsFieldRequired('İleri Tar.Arama', 'CD_ILERI_ARA') || isIleriAramaRequiredByDegerlendirme) && <RequiredRule message={Dict.get('İleri Tar.Arama') + ' ' + Dict.get('isRequired')} />}
                        </SimpleItem>
                        <SimpleItem cssClass="cari-arama-panel__date">
                            <Label text={Dict.get('Randevu Tarihi')}></Label>
                            <QuickDateTimeBox
                                value={activeArama?.CD_RAN_TAR}
                                readOnly={getFieldReadOnly('Randevu Tarihi', 'CD_RAN_TAR')}
                                onValueChanged={(v) => setAramaDateField('CD_RAN_TAR', v)}
                            />
                            {(getIsFieldRequired('Randevu Tarihi', 'CD_RAN_TAR') || isRandevuRequiredByDegerlendirme) && <RequiredRule message={Dict.get('Randevu Tarihi') + ' ' + Dict.get('isRequired')} />}
                        </SimpleItem>
                    </GroupItem>
                    <GroupItem colCount={2}>
                        <SimpleItem
                            dataField='CD_SAT_REF'
                            editorType='dxSelectBox'
                            visible={isSaticiRequiredByDegerlendirme || !!activeArama?.CD_SAT_REF}
                            editorOptions={{
                                readOnly: getFieldReadOnly('Satıcı', 'CD_SAT_REF'),
                                items: saticiKullaniciItems,
                                searchEnabled: true,
                                searchMode: 'contains',
                                displayExpr: 'KUL_ADI',
                                searchExpr: 'KUL_ADI',
                                valueExpr: 'KUL_REF',
                                showClearButton: true,
                            }}
                        >
                            {isSaticiRequiredByDegerlendirme && <RequiredRule message={`${Dict.get('Satıcı')} ${Dict.get('isRequired')}`} />}
                            <Label text={Dict.get('Satıcı')}></Label>
                        </SimpleItem>
                    </GroupItem>
                    <GroupItem colCount={1}>
                        <GroupItem colCount={1}>
                            <SimpleItem
                                dataField="CD_CFG_REF"
                                editorType="dxSelectBox"
                                visible={showMetaFormSelect}
                                cssClass={META_FORM_FIELD_CLASS}
                                editorOptions={{
                                    items: metaFormItems,
                                    displayExpr: 'META_FORM_LABEL',
                                    valueExpr: 'CFG_REF',
                                    searchEnabled: true,
                                    showClearButton: true,
                                    placeholder: 'Meta form seçin',
                                    readOnly: aramaReadOnly || !!activeArama?._READ_ONLY_FROM_PARA || getFieldReadOnly('Meta_Form', 'CD_CFG_REF'),
                                    elementAttr: { class: META_FORM_EDITOR_CLASS },
                                }}
                            >
                                <Label location="top" text="Meta_Form" />
                            </SimpleItem>
                            <SimpleItem dataField='CD_ACK' editorType="dxTextArea" editorOptions={addMaxLengthToEditorOptions('CARI_DETAY', 'CD_ACK', {
                                readOnly: getFieldReadOnly('Açıklama', 'CD_ACK'),
                                rows: 3
                            })}>
                                {getIsFieldRequired('Açıklama', 'CD_ACK') && <RequiredRule message={Dict.get('Açıklama') + ' ' + Dict.get('isRequired')} />}
                                <Label location={'top'} text={Dict.get('Açıklama')}></Label>
                            </SimpleItem>
                        </GroupItem>
                        <SimpleItem render={() => (
                                <div className="cari-arama-panel__debt-grid">
                                    <div className="cari-arama-panel__subheading">
                                        <span>Borç-Alınan İşlemleri:</span>
                                    </div>
                                    <DataGrid
                                        layoutKey="cari:arama-borc"
                                        dataSource={tahsilatList}
                                        showBorders={true}
                                        keyExpr="CAS_REF"
                                        focusedRowEnabled={true}
                                        focusedRowKey={focusedTahsilatKey}
                                        height={tahsilatGridHeight}
                                        columnAutoWidth={false}
                                        columnMinWidth={80}
                                        hoverStateEnabled={true}
                                        onFocusedRowChanged={(e) => {
                                            if (e.row?.data) handleTahsilatRowFocus(e.row.data);
                                        }}
                                        onRowPrepared={(e) => {
                                            if (e.rowType !== 'data') return;
                                            e.rowElement.classList.toggle('cari-arama-panel__open-balance', hasOutstandingBalance(e.data?.KALAN));
                                        }}
                                    >
                                        <Scrolling mode="standard" useNative={false} showScrollbar="always" columnRenderingMode="standard" />
                                        <LoadPanel enabled={true} />
                                        <Column dataField="CAS_REF" caption="Ref" width={60} visible={false} />
                                        <Column dataField="SATIS_TIP_AD" caption={Dict.get('Satış Tipi')} width={110} />
                                        <Column dataField="SATIS_TURU_AD" caption={Dict.get('Satış Türü')} minWidth={160} />
                                        <Column dataField="PAKET_ADI" caption={Dict.get('Satış Paketi')} minWidth={120} />
                                        <Column dataField="CAS_GIR_TAR" caption={Dict.get('Tarih')} dataType="date" format="dd-MM-yyyy" width={95} />
                                        <Column dataField="TOPLAM_ALACAK" caption={Dict.get('Alacak')} dataType="number" format="#,##0.00" width={90} />
                                        <Column
                                            dataField="KALAN"
                                            caption={Dict.get('Kalan')}
                                            dataType="number"
                                            format="#,##0.00"
                                            width={115}
                                            alignment="right"
                                            cellRender={({ data, displayValue }) => (
                                                <span className="cari-arama-panel__balance">
                                                    <span>{displayValue}</span>
                                                    {hasOutstandingBalance(data?.KALAN) && <small>Açık hesap</small>}
                                                </span>
                                            )}
                                        />
                                    </DataGrid>
                                </div>
                        )} />
                    </GroupItem>

                </GroupItem>
            </Form>

            {/* İşlem gridi - arama gridinin üstünde, yalnızca Gerçekleşti / CI_DETAY işaretlenebilir */}
            <section className="cari-arama-panel__section">
                <div className="cari-arama-panel__section-heading">
                    <button
                        type="button"
                        className="cari-arama-panel__collapse-button"
                        aria-expanded={islemSectionOpen}
                        onClick={() => setIslemSectionOpen((open) => !open)}
                    >
                        <span>{islemSectionOpen ? '▼' : '▶'}</span> 🔧 İşlem Kayıtları ({displayedIslemList.length})
                    </button>
                    {islemSectionOpen && <label className="cari-arama-panel__show-all">
                        <input
                            type="checkbox"
                            checked={islemShowAll}
                            onChange={(e) => setIslemShowAll(e.target.checked)}
                        />
                        Tümünü göster
                    </label>}
                </div>
                {islemSectionOpen && !islemShowAll && selectedTahsilatCasRef && (
                    <div className="cari-arama-panel__filter-note">
                        Filtre: Satış #{selectedTahsilatCasRef}
                        {(tahsilatList || []).find((r) => Number(r.CAS_REF) === Number(selectedTahsilatCasRef))?.SATIS_TIP_AD
                            ? ` — ${(tahsilatList || []).find((r) => Number(r.CAS_REF) === Number(selectedTahsilatCasRef))?.SATIS_TIP_AD}`
                            : ''}
                    </div>
                )}
                {islemSectionOpen && <DataGrid
                    layoutKey="cari:arama-islem"
                    dataSource={displayedIslemList}
                    showBorders={true}
                    keyExpr="CI_REF"
                    height={islemGridHeight}
                    columnAutoWidth={false}
                    columnMinWidth={120}
                    focusedRowEnabled={true}
                    hoverStateEnabled={false}
                    onRowPrepared={(e) => {
                        if (e.rowType === 'data') {
                            const { CI_ISL_TAR, CI_RAN_TAR } = e.data || {};
                            if (CI_ISL_TAR && CI_RAN_TAR) {
                                const islemTarih = new Date(CI_ISL_TAR);
                                const randevuTarih = new Date(CI_RAN_TAR);
                                if (islemTarih > randevuTarih) {
                                    e.rowElement.style.background = '#d32f2f';
                                    e.rowElement.style.color = '#fff';
                                } else {
                                    e.rowElement.style.background = 'lime';
                                    e.rowElement.style.color = '#111';
                                }
                            } else {
                                e.rowElement.style.background = '';
                                e.rowElement.style.color = '';
                            }
                            e.rowElement.style.fontWeight = '';
                        }
                    }}
                >
                    <Scrolling mode="standard" useNative={false} showScrollbar="always" columnRenderingMode="standard" />
                    <LoadPanel enabled={true} />
                    <Column
                        dataField="_GERCEKLESTI"
                        caption="Gerçekleşti"
                        dataType="boolean"
                        width={220}
                        minWidth={180}
                        alignment="left"
                        allowSorting={false}
                        allowFiltering={false}
                        cellRender={({ data }) => (
                            <IslemGerceklestiCell
                                data={data}
                                readOnly={false}
                                islemList={islemList}
                                casRef={data?.CI_CAS_REF}
                                sablonLines={glb.default.islemsablonLkp?.detay || []}
                                onPersistUpdates={persistIslemUpdates}
                            />
                        )}
                    />
                    <Column dataField="SATIS_TIP_AD" caption={Dict.get('Satış Tipi')} width={160} allowEditing={false} />
                    <Column dataField="PAKET_ICERIK_AD" caption={Dict.get('Paket İçeriği')} width={180} allowEditing={false} />
                    <Column dataField="CAS_TIP" caption="CAS_TIP" visible={false} allowEditing={false} />
                    <Column dataField="CI_TUR" caption="CI_TUR" visible={false} allowEditing={false} />
                    <Column dataField="CI_RAN_TAR" dataType="date" format="dd-MM-yyyy" caption={Dict.get('Randevu')} width={120} allowEditing={false} />
                    <Column
                        dataField="CI_ISL_TAR"
                        caption={Dict.get('Gerçekleşme')}
                        width={160}
                        allowEditing={false}
                        cellRender={({ data }) => (
                            <DateBox
                                value={data?.CI_ISL_TAR ?? null}
                                type="datetime"
                                displayFormat="dd-MM-yyyy HH:mm"
                                width="100%"
                                onValueChanged={(e) => {
                                    const prev = data?.CI_ISL_TAR;
                                    if (e.value === prev || (e.value == null && prev == null)) return;
                                    void applyAramaIslemDateChange(data, e.value, prev);
                                }}
                            />
                        )}
                    />
                    <Column
                        dataField="CI_ACK"
                        caption={Dict.get('Açıklama')}
                        minWidth={140}
                        allowEditing={false}
                        cellRender={({ data }) => (
                            <TextBox
                                key={`ack-${data?.CI_REF}-${String(data?.CI_ACK ?? '')}`}
                                defaultValue={data?.CI_ACK ?? ''}
                                width="100%"
                                onEnterKey={(e) => { void saveAramaIslemAck(data, e.component.option('value')); }}
                                onFocusOut={(e) => { void saveAramaIslemAck(data, e.component.option('value')); }}
                            />
                        )}
                    />
                    <Column
                        dataField="CI_UPD_KUL"
                        caption={Dict.get('Gerçekleştiren') || 'Gerçekleştiren'}
                        width={150}
                        allowEditing={false}
                        cellRender={({ data }) => {
                            const kulRef = data?.CI_UPD_KUL ?? data?.ci_upd_kul ?? null;
                            if (isAdminOrGeneralAuth) {
                                return (
                                    <SelectBox
                                        dataSource={kulLookup}
                                        displayExpr="KUL_ADI"
                                        valueExpr="KUL_REF"
                                        value={kulRef}
                                        searchEnabled={true}
                                        searchMode="contains"
                                        searchExpr="KUL_ADI"
                                        showClearButton={true}
                                        width="100%"
                                        onValueChanged={(e) => {
                                            if (Number(e.value ?? 0) === Number(kulRef ?? 0)) return;
                                            void saveAramaIslemUpdKul(data, e.value);
                                        }}
                                    />
                                );
                            }
                            return (
                                <span style={{ fontSize: 12, lineHeight: 1.4 }}>
                                    {getKulAdi(kulRef) || '—'}
                                </span>
                            );
                        }}
                    />
                </DataGrid>}
            </section>

            <section className="cari-arama-panel__section">
                <div className="cari-arama-panel__section-heading">📋 Arama Geçmişi</div>
                <DataGrid
                layoutKey="cari:arama-gecmis"
                className="cari-arama-panel__history-grid"
                dataSource={mergedAramaList}
                showBorders={true}
                keyExpr="CD_REF"
                focusedRowEnabled={!isAramaDraftRow(activeArama)}
                hoverStateEnabled={false}
                repaintChangesOnly={true}
                ref={AramaListGridRef}
                height={aramaGridHeight}
                width={'100%'}
                columnMinWidth={64}
                defaultFocusedRowIndex={0}
                allowColumnResizing={true}
                onFocusedRowChanged={(e) => {
                    if (isAramaDraftRow(activeArama)) return;
                    const active = e.row?.data || null;
                    const currentRef = String(activeArama?.CD_REF ?? activeArama?.cd_ref ?? '');
                    const nextRef = String(active?.CD_REF ?? active?.cd_ref ?? '');
                    if (
                        active &&
                        currentRef === nextRef &&
                        aramaRowSyncKey(active) === aramaRowSyncKey(activeArama)
                    ) {
                        setAramaReadOnly(true);
                        return;
                    }
                    if (currentRef !== nextRef) {
                        setActiveArama(active);
                    }
                    setAramaReadOnly(true);
                }}
                onContentReady={(e) => {
                    e.component.getVisibleRows().forEach((row) => {
                        if (row?.rowType !== 'data' || !row?.data) return;
                        const hasAck = row.data.CD_ACK && row.data.CD_ACK.trim() !== "";
                        const hasAmount = Number(row.data.CD_VER_FIYAT) !== 0;
                        if (hasAck || hasAmount) {
                            const keyStr = String(row.key ?? '');
                            if (expandedAramaMasterKeysRef.current.has(keyStr)) return;
                            const isExpanded = e.component.isRowExpanded(row.key);
                            if (!isExpanded) {
                                expandedAramaMasterKeysRef.current.add(keyStr);
                                e.component.expandRow(row.key);
                            }
                        }
                    });
                }}
                onRowPrepared={(e) => {
                    if (e.rowType === 'data') {
                        // Değerlendirme veya Durum sabit referansını bul
                        let sabit = null;
                        if (e.data.CD_DEG_REF) {
                            sabit = glb.default.sabitLkp.find(x => x.ST_REF === e.data.CD_DEG_REF);
                        } else if (e.data.CD_DURUM_REF) {
                            sabit = glb.default.sabitLkp.find(x => x.ST_REF === e.data.CD_DURUM_REF);
                        }
                        if (sabit && sabit.ST_ZEMIN_RENK && sabit.ST_YAZI_RENK) {
                            e.rowElement.style.background = sabit.ST_ZEMIN_RENK;
                            e.rowElement.style.color = sabit.ST_YAZI_RENK;
                            e.rowElement.style.fontWeight = 'bold';
                        } else {
                            const degAd = String(e.data.CD_DEGERLENDIRME || '').toLowerCase();
                            if (degAd.includes('metadan yeni kayıt')) {
                                e.rowElement.style.background = '#FFE0B2';
                                e.rowElement.style.color = '#E65100';
                                e.rowElement.style.fontWeight = 'bold';
                            }
                        }
                    }
                }}
            >
                <Scrolling mode="standard" useNative={false} showScrollbar="always" columnRenderingMode="standard" />
                <Paging enabled={false} />
                <Pager visible={false} />
                <LoadPanel enabled={true} />
                <HeaderFilter visible={true} />

                <Column dataField="CD_ARA_TAR" width={108} dataType="date" format="dd-MM-yyyy HH:mm" caption={Dict.get('Arama Tarihi')} sortOrder="desc" sortIndex={0} />
                <Column
                    dataField="CD_CFG_REF"
                    caption="Meta_Form"
                    width={160}
                    visible={showMetaFormSelect}
                    cellRender={({ data }) => {
                        const ref = data?.CD_CFG_REF;
                        const item = metaFormItems.find((x) => Number(x.CFG_REF) === Number(ref));
                        return (
                            <span className="meta-form-grid-cell">
                                {item?.META_FORM_LABEL || (ref ? `#${ref}` : '—')}
                            </span>
                        );
                    }}
                />
                <Column dataField='CD_YETKILI' width={108} caption={Dict.get('Arayan Kişi')} />
                <Column dataField="CD_ILERI_ARA" width={108} dataType="date" format="dd-MM-yyyy HH:mm" caption={Dict.get('İleri Tar.Arama')} />
                <Column dataField="CD_RAN_TAR" width={108} dataType="date" format="dd-MM-yyyy HH:mm" caption={Dict.get('Randevu Tarihi')} />
                <Column dataField='CD_DEGERLENDIRME' width={136} caption={Dict.get('Değerlendirme')} />
                <Column
                    dataField="CD_VER_FIYAT"
                    width={76}
                    dataType="number"
                    caption={Dict.get('Tutar')}
                    alignment="right"
                    cellRender={({ data }) => {
                        const amount = Number(data?.CD_VER_FIYAT) || 0;
                        if (amount === 0) return <span>-</span>;
                        const color = amount < 0 ? '#d32f2f' : '#2e7d32';
                        return (
                            <span style={{ color, fontWeight: 700 }}>
                                {formatSignedMoney(amount)}
                            </span>
                        );
                    }}
                />
                <Column
                    dataField="SON_DURUM"
                    width={96}
                    caption={Dict.get('Son Durum')}
                    cellRender={({ data, displayValue }) => data?._READ_ONLY_FROM_PARA ? '—' : displayValue}
                />
                <Column {...addMaxLengthToGridColumn('CARI_DETAY', 'CD_ACK', {
                        dataField: "CD_ACK", 
                        caption: Dict.get('Açıklama')
                    })} width={90} visible={false} />
                <Column dataField="CD_REF" visible={false} caption={Dict.get('id')} />

                <MasterDetail
                    enabled={true}
                    render={(props) => (
                        <div style={{ background: "var(--dx-color-bg-secondary, #f8f9fa)", padding: 8 }}>
                            {props.data.CD_ACK && String(props.data.CD_ACK).trim() !== '' && (
                                <div style={{
                                    color: Number(props.data.CD_VER_FIYAT) < 0 ? '#d32f2f' : (Number(props.data.CD_VER_FIYAT) > 0 ? '#2e7d32' : 'inherit'),
                                    fontWeight: Number(props.data.CD_VER_FIYAT) !== 0 ? 700 : 400
                                }}>
                                    Açıklama: {props.data.CD_ACK}
                                </div>
                            )}
                        </div>
                    )}
                />
                </DataGrid>
            </section>
        </div>
    );
}
